import { Table, TableContainer } from '@mui/material';
import { useCallback } from 'react';

import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { useTablePagination, useTableSort } from '../../../hooks';
import { Button, Loader, Pagination, TableHead } from '../../Shared';
import { getFormattedCallLogs } from './helper';
import { TableBody } from './TableBody';

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const getState = (data, isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  const hasNoData = data.length === 0;
  if (hasNoData) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

const CELLS = [
  { id: 'tenant', label: 'Client', sortable: true },
  { id: 'callerName', label: 'Caller Name', sortable: true },
  { id: 'contactCompany', label: 'Contact Company', sortable: true },
  { id: 'contactName', label: 'Contact Name', sortable: true },
  { id: 'phoneNumber', label: 'Phone', sortable: false },
  { id: 'createdAt', label: 'Date & Time', sortable: true },
  { id: 'callDuration', label: 'Duration', sortable: false },
  { id: 'callOutcome', label: 'Outcome', sortable: false },
  { id: 'details', label: '', width: '56px', sortable: false },
];

export const CallLogsTable = ({
  callLogs,
  selectedCallRecordingIDs,
  isCallLogsLoading,
  isCallLogsError,
  fetchCallLogs,
  onChangeSelectedCallRecordingIDs,
}) => {
  const { order, orderBy, onRequestSort, sortedRows } = useTableSort(
    'createdAt',
    getFormattedCallLogs(callLogs),
  );
  const { page, count, rowsByPage, counter, setPage } =
    useTablePagination(sortedRows);

  const onSelectAllRows = event => {
    const checked = event.target.checked;

    onChangeSelectedCallRecordingIDs(prev => {
      const newSelectedCallRecordingIDs = new Map(prev);

      if (checked) {
        sortedRows.forEach(({ callRecordingID }) =>
          newSelectedCallRecordingIDs.set(callRecordingID, callRecordingID),
        );
      } else {
        newSelectedCallRecordingIDs.clear();
      }

      return newSelectedCallRecordingIDs;
    });
  };

  const onSelectRow = useCallback(
    selectedCallRecordingID => {
      onChangeSelectedCallRecordingIDs(prev => {
        const newSelectedCallRecordingIDs = new Map(prev);
        const isSelected = newSelectedCallRecordingIDs.has(
          selectedCallRecordingID,
        );

        if (isSelected) {
          newSelectedCallRecordingIDs.delete(selectedCallRecordingID);
        } else {
          newSelectedCallRecordingIDs.set(
            selectedCallRecordingID,
            selectedCallRecordingID,
          );
        }

        return newSelectedCallRecordingIDs;
      });
    },
    [onChangeSelectedCallRecordingIDs],
  );

  const isSomeRowsSelected =
    selectedCallRecordingIDs.size > 0 &&
    selectedCallRecordingIDs.size < sortedRows.length;
  const isAllRowsSelected =
    sortedRows.length > 0 &&
    selectedCallRecordingIDs.size === sortedRows.length;
  const state = getState(sortedRows, isCallLogsLoading, isCallLogsError);

  return (
    <>
      {(state === STATE.HAS_DATA || state === STATE.HAS_NO_DATA) && (
        <>
          <TableContainer
            className={state === STATE.HAS_DATA ? 'h-full' : undefined}
          >
            <Table stickyHeader>
              <TableHead
                cells={CELLS}
                isSomeRowsSelected={isSomeRowsSelected}
                isAllRowsSelected={isAllRowsSelected}
                onSelectAllRows={onSelectAllRows}
                order={order}
                orderBy={orderBy}
                onRequestSort={onRequestSort}
              />
              {state === STATE.HAS_DATA && (
                <TableBody
                  callLogs={rowsByPage}
                  selectedCallRecordingIDs={selectedCallRecordingIDs}
                  onSelectRow={onSelectRow}
                />
              )}
            </Table>
          </TableContainer>
          {state === STATE.HAS_DATA && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-base-800 text-[14px]/[20px]">
                {counter}
              </span>
              <Pagination count={count} page={page} onChange={setPage} />
            </div>
          )}
        </>
      )}
      {state === STATE.IS_DATA_LOADING && (
        <div className="h-full flex justify-center items-center">
          <Loader />
        </div>
      )}
      {state === STATE.IS_DATA_ERROR && (
        <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
          <div className="flex justify-center items-center flex-col gap-1.5">
            <span className="text-base-1000 text-[22px]/[28px] font-semibold">
              Failed to load records
            </span>
            <span className="text-base-800 text-[16px]/[24px]">
              Error occurred while loading. Try again.
            </span>
          </div>
          <Button
            title="Reload"
            variant="text"
            className="text-destructive-500 !bg-transparent"
            iconStart={<RefreshIcon className="w-5 h-5 text-destructive-500" />}
            onClick={fetchCallLogs}
          />
        </div>
      )}
      {state === STATE.HAS_NO_DATA && (
        <div className="flex justify-center pt-2 px-6 pb-6">
          <span className="text-base-800 text-[16px]/[24px]">
            No available records
          </span>
        </div>
      )}
    </>
  );
};
